import { DeviceOutcome } from './types';

/**
 * Extracts a comparable error name from whatever `getUserMedia` (or, defensively,
 * an Agora track-creation call) threw.
 *
 * - Standard browser failures are `DOMException`s with a `.name`
 *   (NotAllowedError, NotFoundError, NotReadableError, OverconstrainedError, ...).
 * - Agora wraps failures in an error carrying a `.code` (e.g. 'PERMISSION_DENIED',
 *   'NOT_READABLE'). We normalise those onto the DOMException names so a single
 *   mapping table covers both.
 */
const getErrorName = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'UnknownError';

  // Agora RTC errors expose a string `code`; map the ones we care about.
  const code = (error as { code?: unknown }).code;
  if (typeof code === 'string') {
    switch (code) {
      case 'PERMISSION_DENIED':
        return 'NotAllowedError';
      case 'DEVICE_NOT_FOUND':
        return 'NotFoundError';
      case 'NOT_READABLE':
      case 'MEDIA_OPTION_INVALID':
        return 'NotReadableError';
      case 'CONSTRAINT_NOT_SATISFIED':
        return 'OverconstrainedError';
      case 'NOT_SUPPORTED':
        return 'NotSupportedError';
      default:
        break;
    }
  }

  const name = (error as { name?: unknown }).name;
  return typeof name === 'string' ? name : 'UnknownError';
};

export type MapAcquisitionErrorOptions = {
  /**
   * When true, a `NotFoundError` is treated as a permission block rather than missing
   * hardware. Firefox on macOS (and some other stacks) throw NotFoundError when the OS
   * privacy layer blocks access, while `enumerateDevices` still lists videoinput/audioinput —
   * which would otherwise route to "no device found" and a futile reconnect poll.
   */
  deviceKindPresent?: boolean;
  /**
   * Whether this engine's `permissions.query` can ever report a *denied* state for camera/mic
   * (`Capabilities.reportsPermissionDenial`). Defaults to `true`.
   *
   * Pass `false` on WebKit, which answers `'prompt'` regardless of what the user chose: there,
   * a post-failure `'prompt'` says nothing, so the Dismissed split below cannot be made from it.
   */
  reportsPermissionDenial?: boolean;
};

/**
 * Pure mapping from an acquisition failure to a {@link DeviceOutcome}.
 *
 * `browserPermissionAfter` is the result of querying `navigator.permissions` for
 * this device kind *after* the failure. It is what lets us split the three cases
 * that share the same `NotAllowedError`:
 *   - permission reports `granted`  → the OS is blocking      → SystemDenied
 *   - permission reports `prompt`   → the prompt was dismissed → Dismissed
 *   - permission reports `denied`/unknown → a real block        → PermissionDenied
 *
 * Pass `null` for `browserPermissionAfter` when the Permissions API is
 * unavailable (e.g. older Safari); we then fall back to the safe `PermissionDenied`.
 *
 * The `prompt → Dismissed` inference holds ONLY where a denial would have shown up as `denied`.
 * On WebKit it never does (the API answers `'prompt'` either way), so callers pass
 * `reportsPermissionDenial: false` and the `'prompt'` reading is discarded as uninformative —
 * without that, an iOS user blocked in the Settings app is told "we'll ask again" forever.
 *
 * Never returns `Granted` — this is only called on the failure path.
 */
export const mapAcquisitionError = (
  error: unknown,
  browserPermissionAfter: PermissionState | null,
  options: MapAcquisitionErrorOptions = {}
): DeviceOutcome => {
  switch (getErrorName(error)) {
    // NotAllowedError (+ legacy aliases some browsers still emit).
    // We trust the post-failure permission STATE over the error name: even if a
    // browser reports `PermissionDismissedError`, a `denied` state means the user
    // is actually blocked, so we return PermissionDenied (not Dismissed) and route
    // them to the reload-and-grant path rather than a futile re-prompt.
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'PermissionDismissedError':
      if (browserPermissionAfter === 'granted') return DeviceOutcome.SystemDenied;
      // `prompt` means "undecided" only on an engine that would have said `denied` otherwise.
      if (browserPermissionAfter === 'prompt' && options.reportsPermissionDenial !== false) {
        return DeviceOutcome.Dismissed;
      }
      return DeviceOutcome.PermissionDenied;

    case 'NotFoundError':
    case 'DevicesNotFoundError':
      // Granted-but-failing only means an OS block when a device of this kind is actually present;
      // if nothing enumerates (camera unplugged after a prior grant) it's missing hardware, not a block.
      if (browserPermissionAfter === 'granted' && options.deviceKindPresent) return DeviceOutcome.SystemDenied;
      // Only trust the "devices enumerate, so it's a block not missing hardware" heuristic when the
      // Permissions API gave a definite answer. With no Permissions API (`null` — iOS Safari), inputs
      // enumerate *before* any grant, so a genuine NotFoundError would be misread as a permission block.
      if (browserPermissionAfter !== null && options.deviceKindPresent) return DeviceOutcome.PermissionDenied;
      return DeviceOutcome.NoDevice;

    // OverconstrainedError: a specific device (e.g. a remembered deviceId) could
    // not satisfy the constraints — usually because it was unplugged.
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return DeviceOutcome.NoDevice;

    // NotReadable: device exists but couldn't be opened. AbortError: read was
    // aborted (e.g. device yanked mid-acquire).
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return DeviceOutcome.DeviceUnavailable;

    case 'SecurityError':
    case 'NotSupportedError':
    case 'TypeError':
      return DeviceOutcome.Unsupported;

    // Safari hangs getUserMedia indefinitely when no hardware is present (lid closed).
    // The timeout wrapper converts the hang into this error name; classify as NoDevice
    // so the "no devices available" warning shows and the user can retry on reconnect.
    case 'AcquireTimeoutError':
      return DeviceOutcome.NoDevice;

    // Unknown failure. Treat as device-unavailable (a real failure the caller must
    // react to) rather than swallowing it back into a "keep waiting" state — that
    // swallow is exactly the stuck-overlay bug on main.
    default:
      return DeviceOutcome.DeviceUnavailable;
  }
};
